import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useProductPriceLines, useProductStock, useQuickSetStock } from '../../hooks';
import { useProductLocationLookup } from '../../lib/useProductLocationLookup';
import { formatQuantity } from '../../lib/format';
import type { PriceLineResponse, Product } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';
import { PriceCellEditorDrawer } from './PriceCellEditorDrawer';
import type { PriceCellEditorContext } from './PriceLineEditorForm';

/**
 * Stable empty fallback for the not-yet-loaded price-lines query. An inline `?? []` would mint
 * a NEW array identity on every parent render, re-firing the drawer's seeding effect (keyed on
 * `existingLines`) while the drawer is open — resetting the form and wiping whatever the
 * merchant already typed (surfaced as "Valid from value not displayed").
 */
const EMPTY_PRICE_LINES: PriceLineResponse[] = [];

interface QuickDialogProps {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

export interface QuickPriceDialogProps extends QuickDialogProps {
  /**
   * The slot the row's displayed price actually resolved to (design spec §8.12,
   * adr-merchant-portal-018 Decision 3 — "edit what you see") — derived by `ProductsPage` from the
   * row's `*Used` provenance, or the scope-declared fallback when that provenance is absent.
   * Defaults to the base slot so a caller that doesn't (yet) compute a scope-aware context — none
   * remain in this codebase, but the fallback keeps this component usable standalone — degrades to
   * the pre-WP2.3 behaviour rather than crashing.
   */
  context?: PriceCellEditorContext;
  /** See `PriceLineEditorForm`'s own doc comment — forwarded as-is. */
  storeCaption?: string;
  /** design spec §4/§8.12 — a `catalog.pricing.view`-only user opens this read-only rather than
   * not at all (plan-merchant-portal-018 WP2.3's gate fix, same class as the R5 fix on the card). */
  readOnly?: boolean;
}

/** Inline price edit through web-price-engine-service price lines. */
export function QuickPriceDialog({
  product,
  open,
  context,
  storeCaption,
  readOnly,
  onClose,
  onResult,
}: QuickPriceDialogProps) {
  const priceLines = useProductPriceLines(product?.id, { enabled: open && Boolean(product?.id) });
  return (
    <PriceCellEditorDrawer
      open={open && Boolean(product)}
      product={product ? { id: product.id, name: product.name } : { id: '' }}
      context={context ?? { type: 'base' }}
      existingLines={priceLines.data ?? EMPTY_PRICE_LINES}
      storeCaption={storeCaption}
      readOnly={readOnly}
      onClose={onClose}
      onResult={onResult}
    />
  );
}

/**
 * Quick stock edit (plan-catalog-007 Phase 2) — SET-TO-VALUE per location, against the ledgered
 * inventory SoR (replaces the retired master stock write): the merchant picks a fulfilment
 * location, sees the current on-hand value there and states the counted quantity; the BFF applies
 * the signed difference as a MANUAL_CORRECTION inventory adjustment. A zero difference writes
 * nothing. Availability chips on the grid follow from the ledgered quantity — there is no manual
 * "availability" flag any more.
 */
export function QuickStockDialog({ product, open, onClose, onResult }: QuickDialogProps) {
  const { t } = useTranslation('catalog');
  const quickSet = useQuickSetStock();
  const { locations, isLoading: locationsLoading } = useProductLocationLookup();
  const stockQuery = useProductStock(open && product ? product.id : undefined);

  const activeLocations = useMemo(() => locations.filter((l) => l.active), [locations]);

  const [locationId, setLocationId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');

  // Preselect the only active location; otherwise the merchant picks one explicitly.
  useEffect(() => {
    if (open) {
      setLocationId(activeLocations.length === 1 ? activeLocations[0].id : '');
      setQuantity('');
      setNote('');
    }
  }, [open, product, activeLocations]);

  const currentOnHand = useMemo(() => {
    if (!locationId) return undefined;
    const row = (stockQuery.data?.items ?? []).find((item) => item.locationId === locationId);
    // No row at this location yet ⇒ the count starts from zero.
    return row ? row.onHandQty : 0;
  }, [stockQuery.data, locationId]);

  // Seed the input with the current value once a location is chosen (count starts from reality).
  useEffect(() => {
    if (open && locationId && currentOnHand !== undefined && quantity === '') {
      setQuantity(String(currentOnHand));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationId, currentOnHand]);

  const parsedQuantity = quantity.trim() === '' ? undefined : Number(quantity);
  const quantityValid =
    parsedQuantity !== undefined && !Number.isNaN(parsedQuantity) && parsedQuantity >= 0;

  const submit = () => {
    if (!product || !locationId || !quantityValid) return;
    quickSet.mutate(
      {
        productId: product.id,
        locationId,
        newQuantity: parsedQuantity,
        note: note.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          onResult(
            'success',
            result.skipped
              ? t('quickStockDialog.unchanged', { name: product.name })
              : t('quickStockDialog.success', { name: product.name }),
          );
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('quickStockDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {product?.name}
          </Typography>
          <TextField
            select
            required
            label={t('quickStockDialog.locationLabel')}
            value={locationId}
            onChange={(e) => {
              setLocationId(e.target.value);
              setQuantity('');
            }}
            disabled={locationsLoading}
            helperText={
              activeLocations.length === 0 && !locationsLoading
                ? t('quickStockDialog.noLocations')
                : undefined
            }
            fullWidth
          >
            {activeLocations.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name} · {l.code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            label={t('quickStockDialog.quantityLabel')}
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            helperText={
              locationId && currentOnHand !== undefined
                ? t('quickStockDialog.currentOnHand', { qty: formatQuantity(currentOnHand) })
                : t('quickStockDialog.quantityHelper')
            }
            error={quantity.trim() !== '' && !quantityValid}
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
          <TextField
            label={t('quickStockDialog.noteLabel')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('quickStockDialog.notePlaceholder')}
            fullWidth
          />
          <Alert severity="info" variant="outlined">
            {t('quickStockDialog.ledgerHint')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={quickSet.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={quickSet.isPending || !locationId || !quantityValid}
          startIcon={quickSet.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
